/**
 * @file Precomputed values and Padé approximant coefficients for
 * trigonometric functions.
 *
 * @internal
 */

import type { TwoF64 } from '../base/common.js';

/**
 * Coefficients (from lowest to highest degree) of the Padé (n,n) approximant
 * for `sin(x)`. Trivial zeros are skipped, which means :
 * - for the numerator:   index `k` maps to degree `2k + 1`
 * - for the denominator: index `k` maps to degree `2k`
 *
 * @internal
 */
export const sin_pade = {
  15: [
    [
      [1.0, 0.0],
      [-0.155042442783908, 4.235519284333916e-18],
      [0.0064635710766700405, 3.332292852582434e-19],
      [-0.0001125542463070811, 2.748128171082249e-21],
      [9.704509120082748e-7, 4.9533542382170916e-23],
      [-4.399746743286081e-9, 4.018630184456407e-26],
      [1.0159119978859635e-11, -3.9608186498795657e-28],
      [-9.571572983324517e-15, 1.7949446393299578e-32],
    ],
    [
      [1.0, 0.0],
      [0.011624223882758675, -3.904099849375701e-19],
      [6.760839046315325e-5, -1.0825850652518662e-21],
      [2.5798482648723524e-7, 1.80978688645186e-23],
      [7.068343627642395e-10, 3.7320900683422285e-26],
      [1.412175530144773e-12, 9.802378605397375e-29],
      [1.940201707178986e-15, 1.2211094360740384e-31],
      [1.4457591944518371e-18, 7.836622649997224e-35],
    ],
  ] as [TwoF64[], TwoF64[]],
  16: [
    [
      [1.0, 0.0],
      [-0.15461956644542246, 4.968006976388968e-18],
      [0.006398980770313209, 6.981795232891738e-20],
      [-0.00010997019239362122, 3.937296893205429e-21],
      [9.288853250139633e-7, -2.4690634979163127e-23],
      [-4.08818994209832e-9, -1.8731125540759433e-25],
      [9.061432622168402e-12, 5.254293534103427e-28],
      [-8.082206136865956e-15, 3.8216777716157134e-31],
    ],
    [
      [1.0, 0.0],
      [0.012047100221244209, 3.4207770711748294e-19],
      [7.34974738539109e-5, 2.1476536793315273e-21],
      [2.9958315102726925e-7, 2.497111666715941e-23],
      [9.026419492744658e-10, 1.0596496623537165e-26],
      [2.0859715153369378e-12, 6.179290755526141e-29],
      [3.6668815315786864e-15, 9.17666347944402e-32],
      [4.565612248930752e-18, 3.627048856281394e-34],
      [3.1513720762224697e-21, -1.8113328046686388e-37],
    ],
  ] as [TwoF64[], TwoF64[]],
  17: [
    [
      [1.0, 0.0],
      [-0.1563818129034934, 1.2722454326416593e-17],
      [0.006672203765959816, -1.838292699616921e-19],
      [-0.00012136059762997271, 4.272493069964215e-21],
      [1.12723364690946e-6, -4.848886597276679e-23],
      [-5.798363624516606e-9, 1.9728460493284842e-25],
      [1.6814870736933406e-11, 3.359483606732881e-28],
      [-2.5985079336728964e-14, 3.951487435053813e-32],
      [1.6867470588031032e-17, -1.0250653599614132e-33],
    ],
    [
      [1.0, 0.0],
      [0.01028485376317328, -5.770923227389274e-19],
      [5.3012726488695916e-5, -2.6362679573051017e-21],
      [1.8044050439770105e-7, 8.70000200746933e-24],
      [4.4800910256129214e-10, 1.521094576530147e-26],
      [8.403551631128341e-13, 2.469040510266714e-29],
      [1.1782802054066227e-15, 9.815721193370643e-32],
      [1.146498662511413e-18, -5.864180868482327e-35],
      [6.035880565762389e-22, -1.1452956915576119e-38],
    ],
  ] as [TwoF64[], TwoF64[]],
  18: [
    [
      [1.0, 0.0],
      [-0.15605276263507858, 2.8683047025522337e-18],
      [0.006621326156118769, -3.55398750995105e-19],
      [-0.00011925501128991996, 1.4073157681616819e-21],
      [1.091047925584711e-6, 5.439199191138431e-23],
      [-5.4927136589941876e-9, 2.754699618245532e-25],
      [1.5469650739155245e-11, 2.6008921787233384e-28],
      [-2.3003412500181773e-14, -9.943956066038074e-31],
      [1.42080184893115e-17, -1.5148958156782917e-33],
    ],
    [
      [1.0, 0.0],
      [0.010613904031588085, -2.2901090742444956e-20],
      [5.697682805011639e-5, -2.7841352475696087e-21],
      [2.046248678971406e-7, 2.153424311062325e-24],
      [5.465870188193684e-10, 3.3041224095909433e-26],
      [1.1373697388040606e-12, -7.683527681685872e-29],
      [1.8646696924338813e-15, -5.019967709441171e-32],
      [2.3530870147226736e-18, -1.3099078799118796e-34],
      [2.1059039925648275e-21, 1.3551678370478453e-37],
      [1.0369598275559271e-24, -3.722299640262222e-42],
    ],
  ] as [TwoF64[], TwoF64[]],
  19: [
    [
      [1.0, 0.0],
      [-0.15744502206678576, 4.3630021122676376e-18],
      [0.0068390502100811245, -1.2272049925929927e-19],
      [-0.00012854444991334965, 3.0151997420467655e-21],
      [1.26001336227266e-6, -1.0212308904713177e-22],
      [-7.062115335641558e-9, -2.9275198569265427e-25],
      [2.3542477183856293e-11, -5.806644701920396e-28],
      [-4.641407487661429e-14, -3.1193355643977378e-31],
      [5.038599027953163e-17, 1.617058994805525e-33],
      [-2.3483895015229578e-20, 6.123502029871527e-37],
    ],
    [
      [1.0, 0.0],
      [0.00922164459988091, -2.6292715700384802e-19],
      [4.2657643394609354e-5, -2.104362599639095e-21],
      [1.308173994427409e-7, 1.3439439089545516e-25],
      [2.9536702470966903e-10, 9.96152536177406e-27],
      [5.136248402724343e-13, -8.824520132901346e-30],
      [6.946772906062161e-16, 4.8214496666213627e-32],
      [7.12615285551435e-19, 6.099345829411435e-36],
      [5.096804162436514e-22, -4.18248758293312e-38],
      [1.9660866292185431e-25, -9.266787129298381e-42],
    ],
  ] as [TwoF64[], TwoF64[]],
  20: [
    [
      [1.0, 0.0],
      [-0.15718169978889246, 7.539437501206548e-18],
      [0.006797958041152513, -3.3698434320902737e-19],
      [-0.00012680090722724593, -4.270141647956646e-21],
      [1.2286108610496064e-6, 8.499746735684477e-23],
      [-6.7748181105858275e-9, 2.588704965144813e-25],
      [2.2096123311354273e-11, -1.3836143665042805e-27],
      [-4.234057120576608e-14, -1.4714919632246786e-30],
      [4.4328679300664436e-17, -1.635469733057546e-33],
      [-1.9742607222273878e-20, 1.0185481863988653e-36],
    ],
    [
      [1.0, 0.0],
      [0.009484966877774215, -5.559387200185511e-19],
      [4.5452520781548065e-5, -3.187942034487452e-21],
      [1.4582066759203273e-7, -7.134982456433723e-24],
      [3.4924931103796075e-10, 1.1487243100067685e-26],
      [6.575533791348612e-13, 3.9543858039934797e-29],
      [9.941720810350149e-16, 3.7558426461774873e-32],
      [1.2036243564817332e-18, 7.657645382467387e-35],
      [1.129300649041583e-21, 2.946872948272184e-38],
      [7.511400992686306e-25, 3.97469654626025e-41],
      [2.7305462387587564e-28, -1.1868670227426474e-44],
    ],
  ] as [TwoF64[], TwoF64[]],
};

/**
 * Coefficients (from lowest to highest degree) of the Padé (n,n) approximant
 * for `cos(x)`. Trivial zeros are skipped, which means index `k` maps to degree
 * `2k` for both the numerator and denominator. For odd `n`, approximants are
 * the same as for `n - 1`.
 *
 * @internal
 */
export const cos_pade = {
  14: [
    [
      [1.0, 0.0],
      [-0.48730659557669176, 2.362304704264388e-17],
      [0.03540110844102879, -9.649780403167382e-19],
      [-0.0009002261699801037, 4.6843624684284594e-20],
      [1.0382477006065152e-5, -1.6261519407288825e-22],
      [-5.969292057754571e-8, -6.458656937374508e-24],
      [1.6855269769525314e-10, 3.8659506548563255e-27],
      [-1.8909853069711272e-13, -1.2531567312873343e-29],
    ],
    [
      [1.0, 0.0],
      [0.012693404423308265, -6.630816210314192e-19],
      [8.114398601625515e-5, -4.511261953888368e-21],
      [3.4286094573508354e-7, -6.104950918171e-24],
      [1.0491257073505818e-9, 9.515261174715011e-26],
      [2.364396124473974e-12, -1.0828732660333292e-28],
      [3.7079381389471275e-15, 3.7108511049828474e-31],
      [3.199101366841776e-18, -1.8432643031357017e-34],
    ],
  ] as [TwoF64[], TwoF64[]],
  16: [
    [
      [1.0, 0.0],
      [-0.48888889724851753, -1.1359321670037738e-18],
      [0.03617329812049827, -1.034684754093593e-18],
      [-0.0009567865924672516, -1.1698553239329895e-20],
      [1.1845533576871193e-5, 3.035102290260808e-22],
      [-7.705015572779503e-8, -5.3923180255586295e-24],
      [2.726366355274084e-10, -1.4712676441061564e-27],
      [-5.00875072274193e-13, -5.485270706317302e-30],
      [3.7887354576155265e-16, 1.9585662430858876e-33],
    ],
    [
      [1.0, 0.0],
      [0.01111110275148247, 5.987913089730332e-19],
      [6.218282957283796e-5, 1.0648758726619385e-21],
      [2.3109656295330712e-7, -6.268061564899982e-24],
      [6.304793957273984e-10, 5.220734037632685e-28],
      [1.3089191264773104e-12, 2.2785581125061293e-29],
      [2.048248760670762e-15, -4.1064773982815196e-32],
      [2.246016112876271e-18, -1.2349612811785978e-34],
      [1.3479931903302257e-21, 8.316275402435155e-38],
    ],
  ] as [TwoF64[], TwoF64[]],
  18: [
    [
      [1.0, 0.0],
      [-0.4901207905537303, 5.081589442351221e-19],
      [0.036776214422173284, 1.4106411355206835e-18],
      [-0.0010016685634203102, -1.166317897978097e-20],
      [1.3047463215798417e-5, -8.283277939367259e-23],
      [-9.223434585441621e-8, -6.107628599388502e-24],
      [3.743790464131262e-10, -1.6712226349492845e-26],
      [-8.759729917872199e-13, 4.957121354690604e-29],
      [1.1066949887087198e-15, 1.3031805818296583e-33],
      [-5.910342214900286e-19, 2.7947949231470963e-36],
    ],
    [
      [1.0, 0.0],
      [0.009879209446269688, 5.081589442351221e-19],
      [4.915247864146284e-5, 2.277276493804458e-21],
      [1.6283786140638506e-7, -8.798154181796615e-24],
      [3.9913245014501976e-10, 1.7484280440300634e-26],
      [7.5761273504069e-13, 4.693322647304153e-29],
      [1.125434612071094e-15, 8.249182796359379e-32],
      [1.2770094367900296e-18, -7.218724988284904e-36],
      [1.0185250492109303e-21, 2.0265398109306052e-38],
      [4.423204213608187e-25, -2.158229136458339e-41],
    ],
  ] as [TwoF64[], TwoF64[]],
  20: [
    [
      [1.0, 0.0],
      [-0.4911069865999882, -6.849494600002582e-18],
      [0.03725997942221392, 2.813522062887453e-18],
      [-0.0010381374742958985, 2.405872916047944e-20],
      [1.4050129124215668e-5, 2.0615096608909324e-22],
      [-1.0549380723091932e-7, 5.640196859459341e-26],
      [4.701946615822354e-10, 8.203829712928732e-27],
      [-1.2767603756890232e-12, 5.989177808725088e-29],
      [2.0823064566330178e-15, -1.7775198142994068e-31],
      [-1.8877181646988247e-18, 1.188468473387386e-34],
      [7.381433744341111e-22, 1.6236571582336182e-38],
    ],
    [
      [1.0, 0.0],
      [0.0088930134000118, 8.939930390464619e-20],
      [3.9819455553158204e-5, 3.1559939612809436e-21],
      [1.1891736907783909e-7, -5.166265161771784e-25],
      [2.6402580208262755e-10, 1.3921291272712476e-26],
      [4.586965456372424e-13, 8.348992493671435e-30],
      [6.366593422484391e-16, 4.54623326492955e-32],
      [7.035239268830948e-19, -4.1639816239603784e-35],
      [5.985448347645127e-22, 4.0823520018209233e-38],
      [3.5830435872921623e-25, 1.252857176027789e-41],
      [1.1621211063604659e-28, 2.5487119285102437e-45],
    ],
  ] as [TwoF64[], TwoF64[]],
};

/**
 * Integer coefficients (from highest to lowest degree) of the Padé (n,n)
 * approximant for `tan(x)`. Trivial zeros are skipped, which means index `k`
 * starting from the last element maps to degree `2k - 1` for the numerator and
 * `2k` for the denominator.
 *
 * @internal
 */
export const tan_pade_int = {
  16: [
    [
      [-136.0, 0.0],
      [406980.0, 0.0],
      [-3.3331662e8, 0.0],
      [1.1043097065e11, 0.0],
      [-1.65646455975e13, 0.0],
      [1.10998184271975e15, 0.0],
      [-2.888798898360375e16, 2.0],
      [1.9189878396251062e17, 1.0],
      [0.0, 0.0],
    ],
    [
      [1.0, 0.0],
      [-9180.0, 0.0],
      [1.322685e7, 0.0],
      [-6.72188517e9, 0.0],
      [1.490818103775e12, 0.0],
      [-1.5073827493725e14, 0.0],
      [6.474894082531875e15, 0.0],
      [-9.285425030444062e16, -1.0],
      [1.9189878396251062e17, 1.0],
    ],
  ] as [TwoF64[], TwoF64[]],
  17: [
    [
      [1.0, 0.0],
      [-11628.0, 0.0],
      [2.136645e7, 0.0],
      [-1.405485081e10, 0.0],
      [4.141161399375e12, 0.0],
      [-5.8141906047225e14, 0.0],
      [3.755438567868487e16, 3.0],
      [-9.594939198125531e17, -37.0],
      [6.33265987076285e18, 321.0],
      [0.0, 0.0],
    ],
    [
      [153.0, 0.0],
      [-581400.0, 0.0],
      [6.1108047e8, 0.0],
      [-2.6503432956e11, 0.0],
      [5.3835098191875e13, 0.0],
      [-5.1799152660255e15, 0.0],
      [2.1665991737702813e17, -3.0],
      [-3.07038054340017e18, -16.0],
      [6.33265987076285e18, 321.0],
    ],
  ] as [TwoF64[], TwoF64[]],
  18: [
    [
      [171.0, 0.0],
      [-813960.0, 0.0],
      [1.08114237e9, 0.0],
      [-6.02350749e11, 0.0],
      [1.61505294575625e14, 0.0],
      [-2.14596489592485e16, 0.0],
      [1.3432914877375744e18, -25.0],
      [-3.377418597740187e19, -1712.0],
      [2.2164309547669976e20, 13283.0],
      [0.0, 0.0],
    ],
    [
      [-1.0, 0.0],
      [14535.0, 0.0],
      [-3.357585e7, 0.0],
      [2.810970162e10, 0.0],
      [-1.0767019638375e13, 0.0],
      [2.034966711652875e15, 0.0],
      [-1.877719283934244e17, 9.0],
      [7.675951358500425e18, 296.0],
      [-1.0765521780296847e20, 4783.0],
      [2.2164309547669976e20, 13283.0],
    ],
  ] as [TwoF64[], TwoF64[]],
  19: [
    [
      [-1.0, 0.0],
      [17955.0, 0.0],
      [-5.148297e7, 0.0],
      [5.40571185e10, 0.0],
      [-2.6428139112375e13, 0.0],
      [6.557114959770375e15, 0.0],
      [-8.315613971708794e17, -15.0],
      [5.06612789661028e19, 2568.0],
      [-1.255977541034632e21, -20657.0],
      [8.200794532637892e21, -327729.0],
      [0.0, 0.0],
    ],
    [
      [-190.0, 0.0],
      [1.119195e6, 0.0],
      [-1.85338692e9, 0.0],
      [1.3050932895e12, 0.0],
      [-4.5221482481175e14, 0.0],
      [8.047368359718187e16, 3.0],
      [-7.16422126793373e18, -208.0],
      [2.870805808079159e20, -1832.0],
      [-3.989575718580596e21, 88586.0],
      [8.200794532637892e21, -327729.0],
    ],
  ] as [TwoF64[], TwoF64[]],
  20: [
    [
      [-210.0, 0.0],
      [1.514205e6, 0.0],
      [-3.0889782e9, 0.0],
      [2.7105783705e12, 0.0],
      [-1.19220271995825e15, 0.0],
      [2.7718713239029312e17, 5.0],
      [-3.377418597740187e19, -1712.0],
      [2.0095640656554112e21, 85480.0],
      [-4.920476719582735e22, -2.22793e6],
      [3.1983098677287775e23, 1.8675849e7],
      [0.0, 0.0],
    ],
    [
      [1.0, 0.0],
      [-21945.0, 0.0],
      [7.7224455e7, 0.0],
      [-1.003917915e11, 0.0],
      [6.1665657928875e13, 0.0],
      [-1.9671344879311124e16, -1.0],
      [3.3262455886835174e18, 60.0],
      [-2.870805808079159e20, 1832.0],
      [1.1303797869311689e22, -338375.0],
      [-1.5581509612011996e23, 1.6712611e7],
      [3.1983098677287775e23, 1.8675849e7],
    ],
  ] as [TwoF64[], TwoF64[]],
};
